import React, { useLayoutEffect, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { verifyToken } from './redux/slices/userSlice';
import { fetchMySubscription } from './redux/slices/paymentSlice';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import Auth from './pages/Auth';
import AuthCallback from './pages/AuthCallback';
import ForgotPassword from './pages/ForgotPassword';
import Payment from './pages/Payment'; // Correctly placed import
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Admin from './pages/Admin';
import { ToastContainer } from 'react-toastify';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useSelector((state) => state.user);
  
  // Wait for loading OR user data to be fetched if we expect to be authenticated
  if (loading || (isAuthenticated && !user)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  return isAuthenticated ? children : <Navigate to="/auth?mode=login" />;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useSelector((state) => state.user);

  // FIXED: Wait for loading OR user data to be fetched if we have a token
  if (loading || (isAuthenticated && !user)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth?mode=login" />;
  }

  if (user?.access_role !== 'admin') {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Token Processor for OAuth redirects
const TokenProcessor = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = location;
  useEffect(() => {
    const params = new URLSearchParams(search);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    if (accessToken) {
      navigate(`/auth/callback?access_token=${accessToken}&refresh_token=${refreshToken || ''}`);
    }
  }, [search, navigate, dispatch]);
  return <Home />;
};

// Auth Wrapper to handle modes (login/register/forgot-password)
const AuthWrapper = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get('mode') || 'login';

  if (mode === 'login') {
    return <Auth />;
  } else if (mode === 'register') {
    return <Auth />; // Assuming Auth handles both login/register
  } else if (mode === 'forgot-password') {
    return <ForgotPassword />;
  }

  // Default to login if mode is invalid
  return <Auth />;
};

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, loading, user } = useSelector((state) => state.user);
  const { subscription } = useSelector((state) => state.payment);
  useLayoutEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;500;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);
  const toastStyles = {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    newestOnTop: true,
    closeOnClick: true,
    rtl: false,
    pauseOnFocusLoss: true,
    draggable: true,
    pauseOnHover: true,
    theme: "light",
    style: {
      zIndex: 9999,
    },
  };
  useEffect(() => {
    document.body.style.fontFamily = "'Rubik', sans-serif";
  }, []);
  // FIXED: Verify token on app load - fetch user data if we have token but no user
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    // Dispatch verifyToken if we have a token BUT no user data yet
    if (token && !user) {
      console.log('🔄 App.js: Fetching user data...');
      dispatch(verifyToken());
    }
  }, [dispatch, user]); // Changed dependency from isAuthenticated to user

  // Fetch subscription when authenticated
  useEffect(() => {
    if (isAuthenticated && !subscription) {
        console.log('💳 App.js: Fetching subscription data...');
        dispatch(fetchMySubscription());
    }
  }, [dispatch, isAuthenticated, subscription]);
  // Show loading spinner while verifying token
  // FIXED: Also show if we have token but user isn't loaded yet (even if loading is not yet true)
  if ((loading || (isAuthenticated && !user)) && localStorage.getItem('accessToken')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }
  return (
    <Router>
      <Routes>
        <Route path="/" element={<TokenProcessor />} />
        <Route
          path="/auth"
          element={isAuthenticated 
            ? <Navigate to={user?.access_role === 'admin' ? "/admin" : "/dashboard"} /> 
            : <AuthWrapper />
          }
        />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} /> {/* Direct route for forgot password */}
        <Route
          path="/payment"
          element={
            <ProtectedRoute>
              <Payment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <Admin />
            </AdminRoute>
          }
        />
      </Routes>
      <ToastContainer {...toastStyles} />
    </Router>
  );
}
export default App;